import { useState, useRef, useEffect, useCallback } from "react";
import { FiSearch, FiEdit, FiLogOut, FiUser, FiUsers,
         FiMoon, FiSun, FiUserPlus, FiShield, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "../../utils/api";
import useAuthStore from "../../store/authStore";
import useChatStore from "../../store/chatStore";
import { useSocket } from "../../context/SocketContext";
import { useTheme } from "../../context/ThemeContext";
import ConversationItem from "./ConversationItem";
import NewChatModal from "./NewChatModal";
import ProfileModal from "./ProfileModal";
import LogoutModal from "./LogoutModal";
import ChangePasswordModal from "./ChangePasswordModal";
import StatusList from "./StatusList";
import { getInitials } from "../../utils/helpers";
import SkeletonLoader from "./SkeletonLoader";
import styles from "./Sidebar.module.css";

const Sidebar = () => {
  const { user, logout } = useAuthStore();
  const {
    conversations, isLoadingConversations, conversationError,
    unreadCounts, mutedConversations,
    addConversation, setActiveConversation,
  } = useChatStore();
  const { isDark, toggleTheme } = useTheme();
  const socketRef = useSocket();
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);
  const activeTabRef = useRef("chats");

  const [search,       setSearch]       = useState("");
  const [showNewChat,  setShowNewChat]  = useState(false);
  const [showProfile,  setShowProfile]  = useState(false);
  const [showLogout,   setShowLogout]   = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [activeTab,    setActiveTab]    = useState("chats");
  const [unviewedStatusCount, setUnviewedStatusCount] = useState(0);

  const [userResults,      setUserResults]      = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  // Keep ref in sync for socket callbacks
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const friendConvs = conversations.filter((c) => !c.isGroup);
  const groupConvs  = conversations.filter((c) =>  c.isGroup);

  const existingFriendIds = new Set(
    friendConvs
      .map((c) => c.participants.find((p) => p._id !== user._id)?._id)
      .filter(Boolean)
  );

  const filteredFriends = friendConvs.filter((c) => {
    const name = c.participants.find((p) => p._id !== user._id)?.name || "";
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const filteredGroups = groupConvs.filter((c) =>
    (c.groupName || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalUnread = Object.entries(unreadCounts || {}).reduce((acc, [id, n]) => {
    return acc + ((mutedConversations || []).includes(id) ? 0 : (n || 0));
  }, 0);

  // Fetch unviewed status count
  const fetchUnviewedCount = useCallback(async () => {
    try {
      const res = await api.get("/status");
      const statuses = res.data.statuses || [];
      const count = statuses.filter(
        (g) => g.user._id !== user._id && g.hasUnviewed
      ).length;
      // Only update badge if not currently on status tab
      if (activeTabRef.current !== "status") {
        setUnviewedStatusCount(count);
      }
    } catch { }
  }, [user._id]);

  useEffect(() => {
    fetchUnviewedCount();
    const interval = setInterval(fetchUnviewedCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnviewedCount]);

  // Real-time status badge
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;
    const onStatusNew = () => {
      if (activeTabRef.current !== "status") {
        setUnviewedStatusCount((c) => c + 1);
      }
    };
    const onStatusDeleted = () => fetchUnviewedCount();
    socket.on("status:new",     onStatusNew);
    socket.on("status:deleted", onStatusDeleted);
    return () => {
      socket.off("status:new",     onStatusNew);
      socket.off("status:deleted", onStatusDeleted);
    };
  }, [socketRef?.current, fetchUnviewedCount]);

  // Search Others
  useEffect(() => {
    if (!search.trim()) {
      setUserResults([]);
      setIsSearchingUsers(false);
      clearTimeout(searchTimeout.current);
      return;
    }
    setIsSearchingUsers(true);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(search.trim())}`);
        const filtered = (res.data.users || []).filter(
          (u) => u._id !== user._id && !existingFriendIds.has(u._id)
        );
        setUserResults(filtered);
      } catch {
        setUserResults([]);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 400);
  }, [search]);

  const handleStartChat = async (targetUserId) => {
    try {
      const res = await api.post("/conversations", { userId: targetUserId });
      const conv = res.data.conversation;
      addConversation(conv);
      setActiveConversation(conv);
      socketRef?.current?.emit("conversation:new", { userId: targetUserId, conversation: conv });
      setSearch("");
      setUserResults([]);
    } catch {
      toast.error("Failed to start conversation");
    }
  };

  const handleModalBtn = (e, setter) => {
    e.preventDefault();
    e.stopPropagation();
    setSearch("");
    setUserResults([]);
    clearTimeout(searchTimeout.current);
    if (searchRef.current) searchRef.current.blur();
    setter(true);
  };

  const handleLogoutConfirm = () => { logout(); setShowLogout(false); };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    activeTabRef.current = tab;
    if (tab === "status") {
      setUnviewedStatusCount(0); // clear badge when opening status tab
    }
  };

  return (
    <aside className={styles.sidebar}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.brand}>
          <span>💬</span>
          <h2>Chatify</h2>
          {/* Badge removed — count shown on Chats tab instead */}
        </div>
        <div className={styles.headerActions}>
          <button className={styles.iconBtn} onMouseDown={(e) => handleModalBtn(e, setShowProfile)} title="Profile">
            <FiUser size={17} />
          </button>
          <button className={styles.iconBtn} onMouseDown={(e) => handleModalBtn(e, setShowChangePw)} title="Change password">
            <FiShield size={17} />
          </button>
          <button className={styles.iconBtn} onMouseDown={(e) => handleModalBtn(e, setShowNewChat)} title="New chat">
            <FiEdit size={17} />
          </button>
          <button className={styles.iconBtn} onMouseDown={(e) => { e.preventDefault(); toggleTheme(); }} title={isDark ? "Light mode" : "Dark mode"}>
            {isDark ? <FiSun size={17} /> : <FiMoon size={17} />}
          </button>
          <button className={styles.iconBtn} onMouseDown={(e) => handleModalBtn(e, setShowLogout)} title="Logout">
            <FiLogOut size={17} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tabBtn} ${activeTab === "chats" ? styles.tabActive : ""}`}
          onClick={() => handleTabSwitch("chats")}
        >
          Chats
          {totalUnread > 0 && activeTab !== "chats" && (
            <span className={styles.tabBadge}>{totalUnread > 99 ? "99+" : totalUnread}</span>
          )}
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === "status" ? styles.tabActive : ""}`}
          onClick={() => handleTabSwitch("status")}
        >
          Status
          {unviewedStatusCount > 0 && activeTab !== "status" && (
            <span className={styles.tabBadge}>{unviewedStatusCount > 99 ? "99+" : unviewedStatusCount}</span>
          )}
        </button>
      </div>

      {/* Status Tab */}
      {activeTab === "status" && <StatusList />}

      {/* Chats Tab */}
      {activeTab === "chats" && (
        <>
          <input type="text" style={{ display: "none" }} autoComplete="username" readOnly />
          <input type="password" style={{ display: "none" }} autoComplete="current-password" readOnly />
          <div className={styles.searchBar}>
            <FiSearch size={15} className={styles.searchIcon} />
            <input
              ref={searchRef}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              placeholder="Search conversations or people..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
            {search && (
              <button className={styles.searchClear} onMouseDown={(e) => e.preventDefault()} onClick={() => { setSearch(""); setUserResults([]); }}>
                <FiX size={13} />
              </button>
            )}
          </div>

          <div className={styles.userInfoSection}>
            <div className={styles.myProfileHeader}>
              <span className={styles.myProfileLabel}>My Profile</span>
              <button className={styles.editProfileBtn} onMouseDown={(e) => handleModalBtn(e, setShowProfile)}>
                <FiEdit size={13} />
              </button>
            </div>
            <div className={styles.userInfo} onMouseDown={(e) => handleModalBtn(e, setShowProfile)}>
              <div className="avatar-wrapper">
                <div className="avatar" style={{ width: 38, height: 38, fontSize: 13 }}>
                  {user?.avatar ? <img src={user.avatar} alt={user.name} /> : getInitials(user?.name)}
                </div>
                <span className="online-dot" />
              </div>
              <div className={styles.userMeta}>
                <span className={styles.userName}>{user?.name}</span>
                <span className={styles.userStatus}>Active now</span>
              </div>
            </div>
          </div>

          <div className={styles.conversationList}>
            {isLoadingConversations ? (
              <SkeletonLoader />
            ) : (
              <>
                <div className={styles.sectionHeader}>
                  <FiUser size={11} className={styles.sectionIcon} />
                  <span className={styles.sectionLabel}>Friends</span>
                  <span className={styles.sectionCount}>{filteredFriends.length}</span>
                </div>
                {conversationError && !filteredFriends.length ? (
                  <div className={styles.sectionEmpty} style={{color:"var(--danger)"}}>
                    ⚠️ Could not load chats. Retrying...
                  </div>
                ) : filteredFriends.length > 0 ? (
                  filteredFriends.map((conv) => <ConversationItem key={conv._id} conversation={conv} />)
                ) : (
                  <div className={styles.sectionEmpty}>
                    {search ? "No friends match your search" : "No conversations yet"}
                  </div>
                )}
                {filteredGroups.length > 0 && (
                  <>
                    <div className={styles.sectionHeader}>
                      <FiUsers size={11} className={styles.sectionIcon} />
                      <span className={styles.sectionLabel}>Groups</span>
                      <span className={styles.sectionCount}>{filteredGroups.length}</span>
                    </div>
                    {filteredGroups.map((conv) => <ConversationItem key={conv._id} conversation={conv} />)}
                  </>
                )}
                {search.trim() && (
                  <div className={styles.sectionHeader} style={{ marginTop: 8 }}>
                    <FiSearch size={11} className={styles.sectionIcon} />
                    <span className={styles.sectionLabel}>Others</span>
                    {!isSearchingUsers && <span className={styles.sectionCount}>{userResults.length}</span>}
                  </div>
                )}
                {search.trim() && (isSearchingUsers ? (
                  <div className={styles.searchingHint}>
                    <span className={styles.searchDot} />
                    <span className={styles.searchDot} />
                    <span className={styles.searchDot} />
                    <span>Searching people…</span>
                  </div>
                ) : userResults.length === 0 ? (
                  <div className={styles.sectionEmpty}>No new people found</div>
                ) : (
                  userResults.map((u) => (
                    <div key={u._id} className={styles.userSearchItem} onClick={() => handleStartChat(u._id)}>
                      <div className="avatar" style={{ width: 40, height: 40, fontSize: 14, flexShrink: 0 }}>
                        {u.avatar
                          ? <img src={u.avatar} alt={u.name} style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
                          : getInitials(u.name)}
                      </div>
                      <div className={styles.userSearchInfo}>
                        <span className={styles.userSearchName}>{u.name}</span>
                        <span className={styles.userSearchEmail}>{u.email}</span>
                      </div>
                      {u.isOnline && <span className={styles.onlinePill}>Online</span>}
                    </div>
                  ))
                ))}
              </>
            )}
          </div>

          <div className={styles.addFriendsBar}>
            <button className={styles.addFriendsBtn} onMouseDown={(e) => handleModalBtn(e, setShowNewChat)}>
              <FiUserPlus size={18} />
              <span>Add Friends</span>
            </button>
          </div>
        </>
      )}

      {showNewChat  && <NewChatModal  onClose={() => setShowNewChat(false)} />}
      {showProfile  && <ProfileModal  onClose={() => setShowProfile(false)} />}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
      {showLogout   && <LogoutModal onConfirm={handleLogoutConfirm} onCancel={() => setShowLogout(false)} />}
    </aside>
  );
};

export default Sidebar;
